import { useState } from 'react';
import { Button } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import ConfirmDeleteModal from 'src/common/components/ConfirmDeleteModal';
import { ApiError, ApiErrorCode } from 'src/error/ApiError';
import { useDisconnectAsset } from 'src/financial/financial-data/hooks/useDisconnectAsset';
import { notifyAndLogError, notifyAndWarn } from 'src/UI/common/ui-helpers';

type Props = {
  clientId: string;
  assetId: string;
  // The form's own name for its provider reference number field, in lower case.
  referenceLabel: string;
  // The form keeps the values it loaded, the connection included, so the page reloads it.
  onDisconnected: (assetId: string) => void;
};

export function FeedDisconnectButton({ clientId, assetId, referenceLabel, onDisconnected }: Props) {
  const [confirmOpened, setConfirmOpened] = useState(false);
  const { disconnectAsset, isLoading } = useDisconnectAsset({ clientId });

  const handleConfirm = async () => {
    try {
      await disconnectAsset(assetId);
      setConfirmOpened(false);
      notifications.show({
        title: 'Disconnected from the data feed',
        message: `You can now edit this asset's ${referenceLabel} and policy number.`,
        color: 'green',
      });
      onDisconnected(assetId);
    } catch (error) {
      if (ApiError.isApiError(ApiErrorCode.ASSET_NOT_CONNECTED_TO_FEED, error)) {
        notifyAndWarn('Already disconnected from the data feed', `${error.message}.`);
        setConfirmOpened(false);
        onDisconnected(assetId);
        return;
      }
      notifyAndLogError(
        'Could not disconnect from the data feed',
        'Something went wrong. Please try again and if the problem persists, contact support.',
        error,
      );
    }
  };

  return (
    <>
      <Button variant="subtle" color="gray" size="xs" onClick={() => setConfirmOpened(true)}>
        Disconnect from the data feed
      </Button>
      <ConfirmDeleteModal
        opened={confirmOpened}
        onClose={() => setConfirmOpened(false)}
        onConfirm={handleConfirm}
        title="Disconnect from the data feed?"
        message={`This asset will stop updating from the data feed and keep its current values. The form reloads, so any changes you haven't saved will be lost. You can then correct its ${referenceLabel} or policy number, and connect it again once the data feed has a wrapper with those numbers.`}
        confirmLabel="Disconnect"
        isLoading={isLoading}
      />
    </>
  );
}
